import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Table2 } from "lucide-react";
import { useReceivablesCustomersQuery } from "../../api/queries/receivablesQueries";
import { displayCurrencyAmount } from "../../utils/display";
import { AGING_SCALE, CHROME, MARKS } from "../../utils/receivablesChartTokens";

/**
 * Who owes the most, and how bad is it.
 *
 * Horizontal stacked bar: horizontal because customer names are long, and
 * stacked because "how much" and "how overdue" are the same question for a
 * collections team. Segments use the validated aging scale — a cool hue for
 * not-yet-due and a single-hue ordinal ramp for the four overdue bands.
 */

const TOP_N = 8;

const AGING_SEGMENTS = [
  { key: "current", label: "Current", color: AGING_SCALE.current },
  { key: "d1_30", label: "1–30 days", color: AGING_SCALE.d1_30 },
  { key: "d31_60", label: "31–60 days", color: AGING_SCALE.d31_60 },
  { key: "d61_90", label: "61–90 days", color: AGING_SCALE.d61_90 },
  { key: "d90_plus", label: "90+ days", color: AGING_SCALE.d90_plus },
] as const;

type DebtorPoint = {
  customerId: number;
  name: string;
  shortName: string;
  current: number;
  d1_30: number;
  d31_60: number;
  d61_90: number;
  d90_plus: number;
  total: number;
  overdue: number;
};

type Props = {
  currency: string;
  consolidated?: boolean;
  startDate?: string;
  endDate?: string;
  managerFilters?: { collectorId?: number; customerGroupId?: number };
};

function truncate(name: string, max = 20): string {
  return name.length <= max ? name : `${name.slice(0, max - 1)}…`;
}

function DebtorsTooltip({
  active,
  payload,
  currency,
}: {
  active?: boolean;
  payload?: ReadonlyArray<{ payload: DebtorPoint }>;
  currency: string;
}) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-md border border-[#E5E7EB] bg-white/95 px-3 py-2 text-xs shadow-lg backdrop-blur-sm">
      <p className="mb-1.5 font-medium text-[#111827]">{point.name}</p>
      {AGING_SEGMENTS.map((segment) => {
        const value = point[segment.key];
        if (!value) return null;
        return (
          <div key={segment.key} className="flex justify-between gap-6">
            <span className="inline-flex items-center gap-1.5 text-[#52514E]">
              <span
                className="h-2 w-2 rounded-sm"
                style={{ backgroundColor: segment.color }}
                aria-hidden
              />
              {segment.label}
            </span>
            <span className="tabular-nums text-[#111827]">
              {displayCurrencyAmount(value, currency)}
            </span>
          </div>
        );
      })}
      <div className="mt-1.5 flex justify-between gap-6 border-t border-[#F3F4F6] pt-1.5">
        <span className="font-medium text-[#52514E]">Total open</span>
        <span className="font-medium tabular-nums text-[#111827]">
          {displayCurrencyAmount(point.total, currency)}
        </span>
      </div>
    </div>
  );
}

function DebtorsTable({
  points,
  currency,
}: {
  points: DebtorPoint[];
  currency: string;
}) {
  return (
    <div className="max-h-80 overflow-auto rounded-lg border border-[#E5E7EB]">
      <table className="w-full text-sm">
        <caption className="sr-only">
          Top customers by open balance, split by aging bucket
        </caption>
        <thead className="sticky top-0 bg-[#FAFAFA] text-xs text-[#52514E]">
          <tr>
            <th scope="col" className="px-3 py-2 text-left font-medium">
              Customer
            </th>
            {AGING_SEGMENTS.map((segment) => (
              <th
                key={segment.key}
                scope="col"
                className="whitespace-nowrap px-3 py-2 text-right font-medium"
              >
                {segment.label}
              </th>
            ))}
            <th scope="col" className="px-3 py-2 text-right font-medium">
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {points.map((point) => (
            <tr key={point.customerId} className="border-t border-[#F3F4F6]">
              <td className="px-3 py-1.5 text-[#111827]">{point.name}</td>
              {AGING_SEGMENTS.map((segment) => (
                <td
                  key={segment.key}
                  className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-[#52514E]"
                >
                  {point[segment.key]
                    ? displayCurrencyAmount(point[segment.key], currency)
                    : "—"}
                </td>
              ))}
              <td className="whitespace-nowrap px-3 py-1.5 text-right font-medium tabular-nums text-[#111827]">
                {displayCurrencyAmount(point.total, currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DashboardTopDebtorsChart({
  currency,
  consolidated,
  startDate,
  endDate,
  managerFilters,
}: Props) {
  const [showTable, setShowTable] = useState(false);

  const customersQuery = useReceivablesCustomersQuery({
    page: 1,
    pageSize: 100,
    currency,
    consolidated,
    riskLevels: [],
    statusFilters: [],
    sorting: [],
    startDate,
    endDate,
    managerFilters,
  });

  const points = useMemo<DebtorPoint[]>(() => {
    const rows = customersQuery.data?.rows ?? [];
    return rows
      .map((row) => {
        const overdue =
          row.oneToThirtyDaysAmount +
          row.thirtyOneToSixtyDaysAmount +
          row.sixtyOneToNinetyDaysAmount +
          row.ninetyOneOrMoreDaysAmount;
        return {
          customerId: row.customerId,
          name: row.customerName,
          shortName: truncate(row.customerName),
          current: row.currentAmount,
          d1_30: row.oneToThirtyDaysAmount,
          d31_60: row.thirtyOneToSixtyDaysAmount,
          d61_90: row.sixtyOneToNinetyDaysAmount,
          d90_plus: row.ninetyOneOrMoreDaysAmount,
          total: row.totalOpenBalance,
          overdue,
        };
      })
      .filter((point) => point.total > 0)
      .sort((a, b) => b.total - a.total)
      .slice(0, TOP_N);
  }, [customersQuery.data?.rows]);

  if (customersQuery.isError) {
    return (
      <div
        role="alert"
        className="rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 text-sm text-[#DC2626]"
      >
        Unable to load top customer balances.
      </div>
    );
  }

  if (customersQuery.isPending) {
    return (
      <div className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
        <div className="mb-3 h-7 w-48 animate-pulse rounded bg-[#F3F4F6]" />
        <div className="h-80 w-full animate-pulse rounded-lg bg-[#F3F4F6]" />
      </div>
    );
  }

  return (
    <article
      aria-label="Top customers by open balance"
      className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm"
    >
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-[#111827]">
            Who owes the most
          </h3>
          <p className="mt-0.5 text-sm text-[#6B7280]">
            Top {Math.min(TOP_N, points.length)} customers by open balance,
            split by age of debt
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowTable((value) => !value)}
          aria-pressed={showTable}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[#E5E7EB] px-2.5 py-1.5 text-xs font-medium text-[#52514E] transition hover:bg-[#FAFAFA]"
        >
          <Table2 className="h-3.5 w-3.5" aria-hidden />
          {showTable ? "Show chart" : "Show table"}
        </button>
      </div>

      {points.length === 0 ? (
        <div className="flex h-72 flex-col items-center justify-center rounded-lg border border-dashed border-[#E5E7EB] bg-[#FAFAFA] px-6 text-center">
          <p className="text-sm font-medium text-[#111827]">
            No open balances
          </p>
          <p className="mt-1 text-sm text-[#6B7280]">
            Nothing outstanding for the selected filters.
          </p>
        </div>
      ) : showTable ? (
        <DebtorsTable points={points} currency={currency} />
      ) : (
        <>
          <div
            className="w-full"
            style={{ height: Math.max(240, points.length * 40 + 48) }}
            role="img"
            aria-label="Top customers by open balance, stacked by aging bucket"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={points}
                layout="vertical"
                margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
                barCategoryGap="28%"
              >
                <CartesianGrid
                  stroke={CHROME.gridline}
                  strokeWidth={1}
                  horizontal={false}
                />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: CHROME.textMuted }}
                  tickLine={false}
                  axisLine={{ stroke: CHROME.axis }}
                  tickFormatter={(value: number) =>
                    displayCurrencyAmount(value, currency)
                  }
                />
                <YAxis
                  type="category"
                  dataKey="shortName"
                  width={128}
                  tick={{ fontSize: 12, fill: CHROME.textSecondary }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  content={<DebtorsTooltip currency={currency} />}
                  cursor={{ fill: "rgba(17,24,39,0.04)" }}
                />
                {AGING_SEGMENTS.map((segment, index) => {
                  const isLast = index === AGING_SEGMENTS.length - 1;
                  return (
                    <Bar
                      key={segment.key}
                      dataKey={segment.key}
                      stackId="aging"
                      name={segment.label}
                      fill={segment.color}
                      maxBarSize={MARKS.maxBarThickness}
                      isAnimationActive={false}
                      radius={isLast ? [0, 4, 4, 0] : undefined}
                    >
                      {/* 2px surface gap between stacked segments — the
                          separator is white space, never a stroke. */}
                      {points.map((point) => (
                        <Cell
                          key={point.customerId}
                          stroke={CHROME.surface}
                          strokeWidth={MARKS.surfaceRingWidth}
                        />
                      ))}
                    </Bar>
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-[#52514E]">
            {AGING_SEGMENTS.map((segment) => (
              <span
                key={segment.key}
                className="inline-flex items-center gap-1.5"
              >
                <span
                  className="h-2.5 w-2.5 rounded-sm"
                  style={{ backgroundColor: segment.color }}
                  aria-hidden
                />
                {segment.label}
              </span>
            ))}
          </div>
        </>
      )}
    </article>
  );
}
